import { useState, useEffect, useCallback } from 'react';
import { urlBase64ToUint8Array } from '@/lib/pushUtils';
import { toast } from 'sonner';

export interface UseWebPushOptions {
  updatePreferences: (updates: Record<string, unknown>) => void;
  preferences?: Record<string, unknown> | null | undefined;
}

export interface UseWebPushReturn {
  pushSupported: boolean;
  pushPermission: NotificationPermission;
  isSubscribing: boolean;
  subscriptionError: string | null;
  requestPushPermission: () => Promise<void>;
  disablePushNotifications: () => Promise<void>;
}

export function useWebPush({ updatePreferences, preferences }: UseWebPushOptions): UseWebPushReturn {
  const [pushSupported, setPushSupported] = useState(false);
  const [pushPermission, setPushPermission] = useState<NotificationPermission>('default');
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState<string | null>(null);

  useEffect(() => {
    if ('Notification' in window && 'serviceWorker' in navigator) {
      setPushSupported(true);
      setPushPermission(Notification.permission);
    }
  }, []);

  const requestPushPermission = useCallback(async () => {
    if (!pushSupported) {
      toast.error('Push notifications are not supported in this browser');
      return;
    }

    setIsSubscribing(true);
    setSubscriptionError(null);

    try {
      const permission = await Notification.requestPermission();
      setPushPermission(permission);

      if (permission === 'granted') {
        const vapidPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;

        if (!vapidPublicKey) {
          updatePreferences({ pushEnabled: true });
          toast.success('Push notifications enabled! (Browser permission granted)');
          setIsSubscribing(false);
          return;
        }

        let registration: ServiceWorkerRegistration;
        try {
          registration = await navigator.serviceWorker.register('/sw.js');
          await navigator.serviceWorker.ready;
        } catch {
          setSubscriptionError('Service worker registration failed');
          toast.error('Failed to register service worker. Please try again.');
          setIsSubscribing(false);
          return;
        }

        try {
          const subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
          });

          updatePreferences({
            pushEnabled: true,
            pushSubscription: subscription.toJSON() as unknown as Record<string, unknown>,
          });
          toast.success('Push notifications enabled successfully!');
        } catch {
          setSubscriptionError('Failed to create push subscription');
          toast.error('Failed to subscribe to push notifications. Please try again.');
          setIsSubscribing(false);
          return;
        }
      } else if (permission === 'denied') {
        setSubscriptionError('Permission denied by user');
        toast.error('Push notification permission denied. Enable in browser settings to continue.');
      } else {
        setSubscriptionError('Permission not granted');
        toast.error('Push notification permission not granted');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setSubscriptionError(errorMessage);
      toast.error('Failed to enable push notifications. Please try again.');
    } finally {
      setIsSubscribing(false);
    }
  }, [pushSupported, updatePreferences]);

  const disablePushNotifications = useCallback(async () => {
    setIsSubscribing(true);
    setSubscriptionError(null);

    try {
      if (preferences?.pushSubscription) {
        try {
          const registration = await navigator.serviceWorker.ready;
          const subscription = await registration.pushManager.getSubscription();
          if (subscription) {
            await subscription.unsubscribe();
          }
        } catch {
          // Continue anyway
        }
      }

      updatePreferences({ pushEnabled: false, pushSubscription: undefined });
      toast.success('Push notifications disabled successfully');
    } catch {
      toast.error('Failed to disable push notifications');
    } finally {
      setIsSubscribing(false);
    }
  }, [preferences?.pushSubscription, updatePreferences]);

  return {
    pushSupported,
    pushPermission,
    isSubscribing,
    subscriptionError,
    requestPushPermission,
    disablePushNotifications,
  };
}
